import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Grid,
    Flex,
    Stack,
    Link,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Banner from '../hero/partials/banner'
import Video from '../hero/partials/video'
import Title from './partials/title'
import PDButton from '../../components/pd-button'
import PDComponent from '../../pd-component'
import {PD_HERO_XX_VALUE_BADGE} from '../../../../constants'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const FullWidthImageHeadlineLink = ({
    regions,
    desktopImage,
    mobileImage,
    imageAlt,

    desktopVideo,
    mobileVideo,
    desktopVideoThumbnail,
    mobileVideoThumbnail,
    videoAlt,
    videoEnableSound,
    videoDisableAutoPlay,

    mediaSize = 'hero',
    desktopMediaPosition = 'full',
    mobileMediaPosition = 'full',
    desktopMediaBackground,
    mobileMediaBackground,
    desktopMediaOverlay,
    mobileMediaOverlay,

    title,
    titleTag,
    titleColor = 'white',
    headlineBackground = 'white',
    desktopTitleSizeOptions,
    mobileTitleSizeOptions,

    buttonText1,
    buttonFontFamily = 'inter',
    buttonLink1,
    buttonBackgroundColor,
    buttonTextColor = 'black',
    buttonType = 'button',
    dropdownLinkLabel1,
    dropdownLinkUrl1,
    dropdownLinkLabel2,
    dropdownLinkUrl2,
    dropdownLinkLabel3,
    dropdownLinkUrl3,
    dropdownLinkLabel4,
    dropdownLinkUrl4,
    dropdownLinkLabel5,
    dropdownLinkUrl5,
    dropdownLinkLabel6,
    dropdownLinkUrl6,

    overlayLink,
    titleFontFamily,
    overlayLinkAriaLabel,

    typeId,
    name
}) => {
    const variant = 'hero'
    const shouldDisplayVideo = (desktopVideo || mobileVideo) && !(desktopImage || mobileImage)
    const hasMedia = shouldDisplayVideo || desktopImage || mobileImage
    const hasButton1 = buttonText1 && buttonLink1
    const hasHeading = title || hasButton1
    const desktopFontSize = desktopTitleSizeOptions === 'medium' ? '36px' : '42px'
    const mobileFontSize = mobileTitleSizeOptions === 'medium' ? '24px' : '42px'
    let links = []
    const pdTagManager = usePdTagManager()
    const isMobile = useBreakpointValue({base: true, md: false})

    const promotionComponents =
        regions?.filter(
            (region) => region?.id === 'heroPromotions' && region?.components?.length > 0
        )?.[0]?.components || []
    const calloutsComponents =
        regions?.filter(
            (region) => region?.id === 'customFlagsCallouts' && region?.components?.length > 0
        )?.[0]?.components || []
    const hasXXValueBadge = calloutsComponents.some(
        (component) => component?.typeId === PD_HERO_XX_VALUE_BADGE
    )
    const mainAreaPadding =
        calloutsComponents.length > 0
            ? hasXXValueBadge
                ? {paddingY: {base: '110px', md: '30px', xl: '90px'}}
                : {paddingY: {base: '70px', md: '30px', xl: '90px'}}
            : {paddingY: {base: '40px', md: '30px', xl: '90px'}}

    if (dropdownLinkLabel1 && dropdownLinkUrl1) {
        links.push({label: dropdownLinkLabel1, url: dropdownLinkUrl1})
    }
    if (dropdownLinkLabel2 && dropdownLinkUrl2) {
        links.push({label: dropdownLinkLabel2, url: dropdownLinkUrl2})
    }
    if (dropdownLinkLabel3 && dropdownLinkUrl3) {
        links.push({label: dropdownLinkLabel3, url: dropdownLinkUrl3})
    }
    if (dropdownLinkLabel4 && dropdownLinkUrl4) {
        links.push({label: dropdownLinkLabel4, url: dropdownLinkUrl4})
    }
    if (dropdownLinkLabel5 && dropdownLinkUrl5) {
        links.push({label: dropdownLinkLabel5, url: dropdownLinkUrl5})
    }
    if (dropdownLinkLabel6 && dropdownLinkUrl6) {
        links.push({label: dropdownLinkLabel6, url: dropdownLinkUrl6})
    }

    useEffect(() => {
        pdTagManager.viewComponent(typeId, name, 1)
    }, [])

    return shouldDisplayVideo || desktopImage || mobileImage ? (
        <Box marginX="0" position="relative">
            <Box position="relative" width="100%" color="white">
                <Grid
                    background={{base: mobileMediaBackground, md: desktopMediaBackground}}
                    position="relative"
                    width="100%"
                    color="white"
                    aspectRatio={{
                        base: '414 / 654',
                        md: mediaSize === 'hero' ? '1920 / 900' : '1920 / 480'
                    }}
                    templateColumns={{
                        base: '1fr',
                        md: desktopMediaPosition === 'full' ? '1fr' : hasMedia ? '1fr 1fr' : '1fr'
                    }}
                    templateRows={{
                        base:
                            mobileMediaPosition === 'full' ? '1fr' : hasMedia ? 'auto 1fr' : '1fr',
                        md: '1fr'
                    }}
                    templateAreas={{
                        base:
                            mobileMediaPosition === 'full'
                                ? `"full"`
                                : hasMedia
                                ? `
                                                "top"
                                                "bottom"
                                            `
                                : `"full"`,
                        md:
                            desktopMediaPosition === 'full'
                                ? `"full"`
                                : hasMedia
                                ? `"left right"`
                                : `"full"`
                    }}
                >
                    <Box
                        gridArea={{base: mobileMediaPosition, md: desktopMediaPosition}}
                        position="relative"
                    >
                        {shouldDisplayVideo ? (
                            <Video
                                desktopVideo={desktopVideo}
                                mobileVideo={mobileVideo || desktopVideo}
                                desktopVideoThumbnail={desktopVideoThumbnail}
                                mobileVideoThumbnail={mobileVideoThumbnail}
                                videoDisableAutoPlay={videoDisableAutoPlay}
                                videoEnableSound={videoEnableSound}
                                videoAlt={videoAlt}
                                variant={variant}
                                mediaSize={mediaSize}
                                desktopMediaPosition={desktopMediaPosition}
                                mobileMediaPosition={mobileMediaPosition}
                            />
                        ) : (
                            <>
                                {desktopImage || mobileImage ? (
                                    <Banner
                                        desktopImage={desktopImage}
                                        mobileImage={mobileImage}
                                        imageAlt={imageAlt}
                                        variant={variant}
                                        mediaSize={mediaSize}
                                        desktopMediaPosition={desktopMediaPosition}
                                        mobileMediaPosition={mobileMediaPosition}
                                    />
                                ) : null}
                            </>
                        )}

                        {desktopMediaOverlay && (
                            <Box
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                background={desktopMediaOverlay}
                            />
                        )}

                        {mobileMediaOverlay && (
                            <Box
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                background={mobileMediaOverlay}
                            />
                        )}

                        {overlayLink && (
                            <Link
                                href={overlayLink}
                                className="overlay-link"
                                zIndex={1}
                                aria-label={overlayLinkAriaLabel || title}
                                position="absolute"
                                top="0"
                                left="0"
                                width="100%"
                                height="100%"
                                onClick={() => {
                                    pdTagManager.selectComponent({
                                        componentId: typeId,
                                        componentName: name,
                                        titleName: title,
                                        titlePosition: 1,
                                        linkUrl: overlayLink || undefined,
                                        imageUrl: isMobile ? mobileImage : desktopImage
                                    })
                                }}
                            />
                        )}
                    </Box>

                    <Grid
                        {...mainAreaPadding}
                        position="relative"
                        gridArea={{
                            base:
                                mobileMediaPosition === 'top' && hasMedia
                                    ? 'bottom'
                                    : mobileMediaPosition === 'bottom' && hasMedia
                                    ? 'top'
                                    : 'full',
                            md:
                                desktopMediaPosition === 'left' && hasMedia
                                    ? 'right'
                                    : desktopMediaPosition === 'right' && hasMedia
                                    ? 'left'
                                    : 'full'
                        }}
                        width="100%"
                        height="100%"
                        px={{
                            base: '20px',
                            md: '30px',
                            xl:
                                desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                    ? '20px'
                                    : '80px'
                        }}
                        templateRows={{base: 'repeat(3, auto)', md: 'repeat(3, auto)'}}
                        templateColumns={{
                            base: '1fr',
                            md: 'repeat(3, auto)',
                            xl:
                                desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                    ? 'repeat(3, auto)'
                                    : 'repeat(3, minmax(auto, 50%))'
                        }}
                        templateAreas={{
                            base: `
                            "top"
                            "middle"
                            "bottom"
                        `,
                            md: `
                            "top-left top-center top-right"
                            "middle-left middle-center middle-right"
                            "bottom-left bottom-center bottom-right"
                        `
                        }}
                    >
                        {promotionComponents.map((component, index) => (
                            <PDComponent
                                key={index}
                                component={component}
                                variant={
                                    desktopMediaPosition === 'left' ||
                                    desktopMediaPosition === 'right'
                                        ? `${mediaSize}Side`
                                        : mediaSize
                                }
                            />
                        ))}
                    </Grid>

                    {calloutsComponents.map((component, index) => (
                        <PDComponent
                            key={index}
                            component={component}
                            variant={
                                desktopMediaPosition === 'left' || desktopMediaPosition === 'right'
                                    ? `${mediaSize}Side`
                                    : mediaSize
                            }
                        />
                    ))}
                </Grid>

                {hasHeading && (
                    <Flex bg={headlineBackground} justifyContent="center" width="100%">
                        <Stack
                            gap="10px"
                            paddingX={{base: '15px', xs: '20px', md: '40px'}}
                            paddingY="40px"
                            textAlign="center"
                            maxWidth={{'2xl': '1520px'}}
                        >
                            <Title
                                title={title}
                                tag={titleTag}
                                color={titleColor}
                                titleFontFamily={titleFontFamily}
                                fontSize={{base: mobileFontSize, md: desktopFontSize}}
                            />

                            {hasButton1 && (
                                <Box>
                                    <PDButton
                                        text={buttonText1}
                                        url={buttonLink1}
                                        buttonFontFamily={buttonFontFamily}
                                        color={buttonTextColor}
                                        ctaType={buttonType}
                                        buttonBackgroundColor={buttonBackgroundColor}
                                        links={links}
                                        typeId={typeId}
                                        name={name}
                                        onClick={() => {
                                            pdTagManager.selectComponent({
                                                componentId: typeId,
                                                componentName: name,
                                                titleName: title,
                                                titlePosition: 1,
                                                linkUrl: overlayLink || undefined
                                            })
                                        }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Flex>
                )}
            </Box>
        </Box>
    ) : null
}

FullWidthImageHeadlineLink.propTypes = {
    desktopImage: PropTypes.string,
    mobileImage: PropTypes.string,
    imageAlt: PropTypes.string,

    desktopVideo: PropTypes.string,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,

    mediaSize: PropTypes.string,
    desktopMediaPosition: PropTypes.string,
    mobileMediaPosition: PropTypes.string,
    desktopMediaBackground: PropTypes.string,
    mobileMediaBackground: PropTypes.string,
    desktopMediaOverlay: PropTypes.string,
    mobileMediaOverlay: PropTypes.string,

    title: PropTypes.string,
    titleTag: PropTypes.string,
    titleColor: PropTypes.string,
    titleFontFamily: PropTypes.string,
    headlineBackground: PropTypes.string,
    desktopTitleSizeOptions: PropTypes.string,
    mobileTitleSizeOptions: PropTypes.string,

    buttonText1: PropTypes.string,
    buttonFontFamily: PropTypes.string,
    buttonLink1: PropTypes.string,

    overlayLink: PropTypes.string,
    overlayLinkAriaLabel: PropTypes.string,

    buttonBackgroundColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonType: PropTypes.string,

    dropdownLinkLabel1: PropTypes.string,
    dropdownLinkUrl1: PropTypes.string,
    dropdownLinkLabel2: PropTypes.string,
    dropdownLinkUrl2: PropTypes.string,
    dropdownLinkLabel3: PropTypes.string,
    dropdownLinkUrl3: PropTypes.string,
    dropdownLinkLabel4: PropTypes.string,
    dropdownLinkUrl4: PropTypes.string,
    dropdownLinkLabel5: PropTypes.string,
    dropdownLinkUrl5: PropTypes.string,
    dropdownLinkLabel6: PropTypes.string,
    dropdownLinkUrl6: PropTypes.string,

    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    typeId: PropTypes.string,
                    data: PropTypes.object
                })
            )
        })
    ),

    typeId: PropTypes.string,
    name: PropTypes.any
}

export default FullWidthImageHeadlineLink
